import { ref, computed, watch } from 'vue';
import type { NotificationResponse } from '@/shared/lib';

export function useNotificationSelection(
  notifications: () => NotificationResponse[],
  onUpdate?: (selectedIds: number[]) => void
) {
  const selectedNotifications = ref<Set<number>>(new Set());

  const isSelected = (id: number) => {
    return selectedNotifications.value.has(id);
  };

  const toggleSelection = (id: number) => {
    if (selectedNotifications.value.has(id)) {
      selectedNotifications.value.delete(id);
    } else {
      selectedNotifications.value.add(id);
    }
  };

  const toggleAll = () => {
    const allCurrentlySelected = notifications().every((notification) =>
      isSelected(notification.id)
    );
    if (allCurrentlySelected) {
      selectedNotifications.value.clear();
    } else {
      notifications().forEach((notification) => {
        selectedNotifications.value.add(notification.id);
      });
    }
  };

  const selectedCount = computed(() => selectedNotifications.value.size);
  const hasSelectedNotifications = computed(() => selectedCount.value > 0);
  const selectedNotificationsArray = computed(() => Array.from(selectedNotifications.value));

  const allSelected = computed(
    () =>
      notifications().length > 0 &&
      notifications().every((notification) => isSelected(notification.id))
  );

  // Watch for changes and emit updates
  watch(
    selectedNotificationsArray,
    (newSelected) => {
      if (onUpdate) {
        onUpdate(newSelected);
      }
    },
    { deep: true }
  );

  const removeFromSelection = (ids: number[]) => {
    ids.forEach((id) => selectedNotifications.value.delete(id));
  };

  return {
    isSelected,
    toggleSelection,
    toggleAll,
    selectedCount,
    hasSelectedNotifications,
    selectedNotificationsArray,
    allSelected,
    removeFromSelection,
  };
}
